import React, { useEffect, useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { useParams } from "react-router-dom"
import { getPageBySlug } from "../services/admin/pages"
import AppLoading from "./AppLoading"
import rehypeRaw from "rehype-raw"

export const MarkdownPage: React.FC = () => {
    const { filename } = useParams()
    const [content, setContent] = useState<string>("")
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [mermaidReady, setMermaidReady] = useState(false)

    // Carrega o script local do Mermaid
    useEffect(() => {
        const loadMermaid = async () => {
            if (!(window as any).mermaid) {
                await new Promise<void>((resolve, reject) => {
                    const script = document.createElement("script")
                    script.src = "/libs/mermaid.min.js"
                    script.onload = () => resolve()
                    script.onerror = () => reject()
                    document.body.appendChild(script)
                })
         }

            (window as any).mermaid.initialize({
                startOnLoad: false,
                theme: "default",
            })

            setMermaidReady(true)
        }

        loadMermaid()
    }, [])

    // Busca o conteúdo da página
    useEffect(() => {
        const getPage = async () => {
            try {
                if (filename) {
                    const data = await getPageBySlug(filename)
                    setContent(data)
                    setError(null)
                }
            } catch (err) {
                console.error("Error loading markdown:", err)
                setError("Erro ao carregar o conteúdo")
                setContent("")
            } finally {
                setLoading(false)
            }
        }

        getPage()
    }, [filename])

    // Renderiza os diagramas Mermaid manualmente
    useEffect(() => {
        const renderMermaidDiagrams = async () => {
            const mermaid = (window as any).mermaid
            if (!mermaid || !content) return

            // Aguarda o DOM estar pronto
            requestAnimationFrame(() => {
                const elements = document.querySelectorAll(".mermaid")
                elements.forEach(async (el, index) => {
                    const graphDefinition = el.textContent || ""
                    try {
                        const { svg, bindFunctions } = await mermaid.render(`mermaid-${index}`, graphDefinition)
                        el.innerHTML = svg
                        bindFunctions?.(el)
                    } catch (err) {
                        console.error(`Erro ao renderizar Mermaid [${index}]:`, err)
                    }
                })
            })
        }

        renderMermaidDiagrams()
    }, [content, mermaidReady])

    if (loading || !mermaidReady) {
        return <AppLoading />
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-red-500">{error}</div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-900 py-8">
            <div className="max-w-4xl mx-auto px-4">
                <div className="prose prose-invert prose-sm sm:prose-base lg:prose-lg max-w-none">
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={{
                            code({ className, children }) {
                                if (className === "language-mermaid") {
                                    return (
                                        <div className="mermaid flex justify-center">
                                            {children}
                                        </div>
                                    )
                                }
                                return <code className={className}>{children}</code>
                            },
                            img({ src, alt }) {
                                return <img src={src} alt={alt} style={{ maxWidth: "100%" }} />
                            },
                        }}
                    >
                        {content}
                    </ReactMarkdown>
                </div>
            </div>
        </div>
    )
}
